import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../hooks/useApi';
import FloorPlanMap from './FloorPlanMap';
import Avatar from '../shared/Avatar';
import WeekPlanner from './WeekPlanner';
import MiniCalendar from './MiniCalendar';
import Modal from '../shared/Modal';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { DESK_BOOKING_TOUR_ID, deskBookingTourSteps } from './deskBookingTour';
import { useDarkMode } from '../../hooks/useDarkMode';
import {
  T, tone, SLOTS, WEEKDAYS, PICKER_ACCENT,
  addDays, isWeekend, todayIso, startOfWeek, endOfWeek, weekDates, clampToWindow,
  fmtDay, fmtShort, fmtLong, fmtRange,
} from './bookingsTheme';
import { weeksAvailable, expandWeek, skipReason } from './repeatWeek';

// ─── Bookings ────────────────────────────────────────────────────────────────
//
// Laid out to match Team Today, which is what the team is used to: days across
// the top, your row of tiles beneath them, the desk picker below that, and the
// month calendar down the right-hand side.
//
// Setting a day and booking a desk are one screen rather than two tabs. They
// were split before, which meant picking Wednesday here and then hunting for it
// again over there.
//
// Bookings run to a 3-week horizon. Days past it are drawn disabled rather than
// hidden, so the limit is visible instead of leaving people wondering where the
// rest of the month went. The server enforces it per office regardless.
//
// Type sizes come from bookingsTheme so the module reads at a normal size
// rather than the 7-10px the rest of the app uses.

const parseZones = (loc) => { try { return JSON.parse(loc?.layout_json || '[]'); } catch { return []; } };

const TABS = [
  { id: 'week', label: 'Desks' },
  { id: 'vans', label: 'Vans' },
  { id: 'team', label: 'Team' },
];

// Small, consistent building blocks so every panel in the module lines up.
// Extra props (e.g. a data-tour anchor) land on the outer div.
const Panel = ({ title, children, className = '', action, ...rest }) => (
  <div className={`bg-white rounded-lg border border-gray-200 ${className}`} {...rest}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200">
        {title && <span className={`${T.label} text-gray-500`}>{title}</span>}
        {action}
      </div>
    )}
    <div className="p-3">{children}</div>
  </div>
);

const Btn = ({ active, className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`px-3 py-2 rounded-md border font-semibold ${T.body} transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      active ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    } ${className}`}
  />
);

export default function MyBookingsPage({ user, initialLocationId }) {
  const [tab, setTab] = useState('week');

  // The picker's accent is set inline rather than by a class, because it is one
  // brand colour rather than a Tailwind shade, so the app's `.dark` stylesheet
  // cannot reach it and the theme has to be read here. Everything else on this
  // page is a standard shade and is covered by that stylesheet. Both uses below
  // are a filled control carrying white text, hence darkFill.
  const isDark = useDarkMode();
  const accent = PICKER_ACCENT[isDark ? 'darkFill' : 'light'];

  // Reference data
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses]   = useState([]);
  const [vans, setVans]           = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [teams, setTeams]         = useState([]);

  // Planner data for the visible week
  const [weekStart, setWeekStart] = useState(() => startOfWeek(todayIso()));
  const [planner, setPlanner]     = useState({ dates: [], people: [], days: {} });
  const [teamFilter, setTeamFilter] = useState('');
  // Your usual home days ('mon,fri'), and what saving a change to them said.
  const [homeDays, setHomeDays] = useState('');
  const [homeDaysMsg, setHomeDaysMsg] = useState(null);

  // Booking form
  const [selectedLocation, setSelectedLocation] = useState('');
  const [pickedDates, setPickedDates] = useState([todayIso()]);
  // One day at a time is the normal case: you book Thursday, then you set Friday
  // to working from home, and those are two separate answers. Picking a day used
  // to add it to the selection, so the second click silently rewrote the first
  // day as well. Selecting several days is still available, but you have to ask
  // for it.
  const [multiDay, setMultiDay]       = useState(false);
  // The day whose "where are you" menu is open, if any. Opened by the icon slot
  // on that day's tile, so the question is asked where it arises rather than in a
  // panel further down the page.
  const [whereMenu, setWhereMenu]     = useState(null);
  // How much of the day the menu's answer covers. Reset to the whole day each
  // time the menu opens, because half days are the exception.
  const [whereSlot, setWhereSlot]     = useState('full');
  const [slot, setSlot]               = useState('full');
  const [repeatOn, setRepeatOn]       = useState(false);
  const [repeatDays, setRepeatDays]   = useState([]);
  // Seeded empty and filled in from the booking window once the offices have
  // loaded (see the clamp below). It used to start at today + 28 days against a
  // three-week window, so the picker opened on a date the hint under it ruled
  // out, and leaving it alone booked half a pattern and a list of refusals.
  const [repeatUntil, setRepeatUntil] = useState('');
  // "Repeat week": the week on screen, done again for the next few weeks.
  const [repeatWeekOpen, setRepeatWeekOpen] = useState(false);
  const [repeatWeeks, setRepeatWeeks]       = useState(1);
  const [guestName, setGuestName]     = useState('');
  // ── BOOKING FOR SOMEBODY ELSE ──────────────────────────────────────────────
  //
  // `bookingFor` is the person this screen is currently acting as, or null for
  // the ordinary case of acting as yourself. Everything below that used to mean
  // "me" reads `subject` instead, so booking for Rachel behaves the way booking
  // for yourself does rather than approximately: her reserved desk unlocks for
  // her, her team is the one haloed on the plan, her seats are the ones offered
  // back to release.
  //
  // It replaced a select buried in the "more options" panel that set one field
  // on the form while the rest of the page carried on being about you. Nobody
  // used it, which is what a control nobody can find amounts to.
  //
  // The draft is held separately from the active subject on purpose: choosing a
  // name does nothing until the button is pressed, so a stray click on a
  // dropdown cannot hand a colleague a desk.
  const [bookingFor, setBookingFor]           = useState(null);
  const [bookingForDraft, setBookingForDraft] = useState('');
  const [people, setPeople]                   = useState([]);
  // Desk awaiting confirmation. Tapping a free desk on the plan opens a confirm
  // step rather than booking outright, so a stray tap cannot take a seat — and
  // the summary spells out what is about to be booked, including the repeat and
  // whose desk it is, both of which are easy to forget.
  const [confirmDesk, setConfirmDesk] = useState(null);
  // Picker controls. A dense office needs a way to narrow it: which part of the floor, and
  // whether to show desks you cannot have. The list is the same information without the
  // plan, for a phone in a corridor or a screen reader.
  const [deskView, setDeskView]   = useState('map');   // map | list
  // The seat you tapped, and where it is on screen, so a card can sit on it.
  const [pickedDesk, setPickedDesk] = useState(null);
  const [deskAnchor, setDeskAnchor] = useState(null);
  // Set instead when the seat was chosen from the plan's dropdown rather than tapped on
  // the plan: the card then opens under the dropdown, where you are already looking.
  const [listAnchor, setListAnchor] = useState(null);
  const [zoneFilter, setZoneFilter] = useState('');
  const [freeOnly, setFreeOnly]   = useState(false);

  // Vans
  const [selectedVan, setSelectedVan] = useState('');
  const [vanNotes, setVanNotes]       = useState('');

  // Every live booking in view, all people. Desk and van availability is read
  // from these rows rather than inferred from the planner text, so a desk is
  // matched by id and "PP-1" can never be confused with "PP-10".
  const [bookings, setBookings] = useState([]);
  // Assigned desks that are open on particular dates because their owner is on
  // leave. { deskId: ['2026-08-12', ...] }, resolved by the server so the screen
  // and the booking endpoint cannot disagree about who may sit where.
  const [released, setReleased] = useState({});
  const [releaseOwners, setReleaseOwners] = useState({});
  const [loading, setLoading] = useState(true);
  // Which loads failed, so a refused or dropped request reads as a failure
  // rather than as "No vans available" or every desk looking free.
  const [loadFailed, setLoadFailed] = useState({});
  const [retrying, setRetrying]     = useState(false);
  const [busy, setBusy]       = useState(false);
  const [banner, setBanner]   = useState(null);   // { kind, title, lines[] }

  // Who may book a desk for somebody else. The same three roles the server
  // enforces in canBookForOthers(): admin, HR and PMO.
  const canBookForOthers = useMemo(
    () => (user?.user_role || '').split(',').map(r => r.trim()).some(r => ['admin', 'hr', 'pmo'].includes(r)),
    [user],
  );

  // The person this screen is about. Normally you; somebody else while the mode
  // is on. Read by every "me" below, which is the whole mechanism.
  const subject = bookingFor || { id: user?.id, name: 'You', first_name: 'You' };
  // How to name them in a sentence, so one label serves both cases.
  const theirs = bookingFor ? `${bookingFor.first_name}'s` : 'your';
  const them   = bookingFor ? bookingFor.first_name : 'you';

  // Everybody but the signed-in user, for the picker. Sorted by the server.
  const bookableFor = useMemo(() => people.filter(p => p.id !== user?.id), [people, user?.id]);

  // Two steps, not one. Starting the mode also clears anything half-set for
  // yourself: a guest name typed a moment ago is not a visitor of Rachel's, and
  // a desk awaiting confirmation was chosen against your own availability.
  const startBookingFor = (id) => {
    const p = people.find(x => x.id === id);
    if (!p) return;
    setBookingFor({ id: p.id, first_name: p.first_name, name: `${p.first_name} ${p.last_name}`.trim() });
    setBookingForDraft('');
    setGuestName('');
    setPickedDesk(null);
    setConfirmDesk(null);
    setBanner(null);
  };

  const stopBookingFor = () => {
    setBookingFor(null);
    setBookingForDraft('');
    setPickedDesk(null);
    setConfirmDesk(null);
    setBanner(null);
  };

  // The guided tour, for people arriving from Team Today. Auto-opens the first
  // time only (the server remembers finished/skipped per person); the Tour
  // button in the header replays it on demand. Held back until the page has
  // loaded so the steps have something to point at.
  const tour = useFeatureTour(DESK_BOOKING_TOUR_ID, { enabled: !loading });
  // The steps all point at the Desks tab, so make sure it is the one showing.
  useEffect(() => { if (tour.open) setTab('week'); }, [tour.open]);

  // Booking for somebody else is a desks-only power, so it ends when the desks
  // tab does. It must not follow anybody onto vans or the team grid, and it must
  // not sit there quietly while they do something else on the page.
  useEffect(() => {
    if (tab !== 'week' && bookingFor) setBookingFor(null);
  }, [tab, bookingFor]);

  const includeWeekend = useMemo(
    () => locations.some(l => l.allow_weekends),
    [locations],
  );
  const visibleDates = useMemo(
    () => weekDates(weekStart, includeWeekend),
    [weekStart, includeWeekend],
  );

  // How far ahead anyone may book. Offices can differ, so the screen shows the
  // tightest of them and the server still enforces each office's own rule.
  const windowDays = useMemo(() => {
    const set = locations.map(l => l.booking_window_days).filter(v => v != null);
    return set.length ? Math.min(...set) : 21;
  }, [locations]);
  // To the END of the week the window lands in, matching the server. Stopping on
  // the day itself left a week you were already looking at half greyed out, with
  // the cut moving a day every morning.
  const maxBookDate = useMemo(() => endOfWeek(addDays(todayIso(), windowDays)), [windowDays]);
  const beyondWindow = (d) => d > maxBookDate;

  // -- TWO KINDS OF OFFICE ----------------------------------------------------
  //
  // Most offices have seats, and being at one means holding one: saying "I am at
  // PP" without booking left the desk reading as free while somebody sat in it.
  // Those offices are answered by picking a desk, so they appear in the desk
  // selector rather than as an answer in the where-are-you dialog.
  //
  // An office with desk_required = 0 has nothing to book, so it stays a one-click
  // answer. The flag lives on the office because which sites have bookable seats
  // is an operations fact that changes without a deploy.
  const deskOffices = useMemo(() => locations.filter(l => l.desk_required !== 0), [locations]);
  const desklessOffices = useMemo(() => locations.filter(l => l.desk_required === 0), [locations]);

  // Sending somebody to the desk selector has to leave them on the day AND the
  // office they were answering for, or they arrive at the right screen showing
  // the wrong date, or whichever office happened to be open last. So the jump
  // sets both selections first and scrolls second.
  const deskPanelRef = useRef(null);
  const goPickADesk = (dates, locationId) => {
    setPickedDates(dates);
    if (locationId) setSelectedLocation(locationId);
    requestAnimationFrame(() => {
      deskPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // The window is not known until the offices are in, and an office can tighten
  // it later, so the repeat's end date follows it rather than being set once.
  useEffect(() => {
    setRepeatUntil(prev => clampToWindow(prev, maxBookDate));
  }, [maxBookDate]);

  // ── ARRIVING TO BOOK A DESK AT A NAMED OFFICE ──────────────────────────────
  //
  // The dashboard's "Today I'm" warning sends people here with the office as the
  // deep-link id (#my-bookings/<office>). Landing has to behave exactly like
  // clicking that office's button in the where-are-you dialog: today picked, the
  // office selected, the desk picker in view. Run once, after the offices have
  // loaded, and only for an office that actually has desks to pick.
  const arriveFor = useRef(initialLocationId || null);
  useEffect(() => {
    if (loading || !arriveFor.current) return;
    const loc = locations.find(l => l.id === arriveFor.current && l.desk_required !== 0);
    arriveFor.current = null;
    if (loc) goPickADesk([todayIso()], loc.id);
    // goPickADesk is stable in behaviour (setters + scroll); this must fire once
    // on the first loaded render only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locations]);

  // ── Loading ────────────────────────────────────────────────────────────────
  const loadReference = useCallback(async () => {
    const failed = {};
    const [locs, sts, vansData, favs, usual] = await Promise.all([
      api.get('desk-locations').catch(() => { failed.offices = true; return []; }),
      api.get('whereabouts/statuses').catch(() => []),
      api.get('bookable-resources', { type: 'van' }).catch(() => { failed.vans = true; return []; }),
      api.get('desk-favourites').catch(() => []),
      api.get('whereabouts/usual').catch(() => null),
    ]);
    setLoadFailed(f => ({ ...f, offices: !!failed.offices, vans: !!failed.vans }));
    setHomeDays(usual?.home_days || '');
    setLocations(locs || []);
    setStatuses((sts || []).filter(s => s.is_selectable));
    setVans(vansData || []);
    setFavourites(favs || []);
    setSelectedLocation(prev => prev || (locs || []).find(l => l.desk_required !== 0)?.id || '');
  }, []);

  const loadWeek = useCallback(async () => {
    const from = visibleDates[0];
    const to   = visibleDates[visibleDates.length - 1];
    if (!from || !to) return;
    const failed = {};
    const [grid, bks, avail] = await Promise.all([
      api.get('whereabouts', { from, to, ...(teamFilter ? { team_id: teamFilter } : {}) }).catch(() => { failed.planner = true; return null; }),
      // A wider window than the grid: "my upcoming" should show the repeat you
      // just booked for next month, not stop at Friday. Desk availability uses
      // the same rows, so one call serves both.
      api.get('bookings', { from, to: addDays(from, 120) }).catch(() => { failed.bookings = true; return []; }),
      api.get('desk-availability', { from, to }).catch(() => null),
    ]);
    setLoadFailed(f => ({ ...f, planner: !!failed.planner, bookings: !!failed.bookings }));
    if (grid) setPlanner(grid);
    setBookings(bks || []);
    setReleased(avail?.released || {});
    setReleaseOwners(avail?.owners || {});
  }, [visibleDates, teamFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadReference();
      setLoading(false);
    })();
  }, [loadReference]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  // People and teams are only needed for the "Booking for" picker and the team
  // filter, so they are not on the critical path.
  useEffect(() => {
    if (!canBookForOthers) return;
    api.get('people').then(p => setPeople(p || [])).catch(() => {});
  }, [canBookForOthers]);
  useEffect(() => {
    api.get('teams').then(t => setTeams(t || [])).catch(() => {});
  }, []);

  // ── Feedback ───────────────────────────────────────────────────────────────
  // A persistent panel rather than a banner that vanishes after four seconds,
  // so a partial result (three booked, two clashed) can actually be read.
  const say = (kind, title, lines = []) => setBanner({ kind, title, lines });
  const clearBanner = () => setBanner(null);

  // The panel sits at the top of the page and the desk was picked half a page
  // down, so a refusal could be reported somewhere nobody was looking and read
  // as nothing happening at all. Bring it into view whenever it changes.
  const bannerRef = useRef(null);
  useEffect(() => {
    if (banner) bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [banner]);

  // How a slot reads in a sentence, as opposed to the button labels ('AM', 'PM').
  const slotWord = s => (s === 'am' ? 'the morning' : s === 'pm' ? 'the afternoon' : 'all day');

  const retryLoad = async () => {
    setRetrying(true);
    try { await Promise.all([loadReference(), loadWeek()]); }
    finally { setRetrying(false); }
  };
  const failedParts = [
    loadFailed.offices && 'the offices and desks',
    loadFailed.vans && 'the vans',
    loadFailed.bookings && 'existing bookings',
    loadFailed.planner && "the week's plan",
  ].filter(Boolean);

  const refresh = async () => { await Promise.all([loadWeek(), api.get('desk-favourites').then(setFavourites).catch(() => {})]); };

  // ── Actions ────────────────────────────────────────────────────────────────
  // ── "IN THE OFFICE" IS NOT AN ANSWER ON ITS OWN ─────────────────────────────
  //
  // "When users say they're in the office, that could be one of a few: PP, RW, Wilmore,
  // Jubilee etc."
  //
  // Quite right, and the old picker had a single "In the office" button, so the answer
  // recorded was the useless half of the question. Somebody deciding whether to come in
  // tomorrow needs to know WHICH office their team will be at.
  //
  // So the location is always sent with the status, never a bare "in the office".
  //
  // Since then the answer has narrowed further. An office with seats is no longer
  // answerable here at all: holding the seat is how you say you are there, and a
  // seatless office day left the desk reading as free to everyone else. The dialog
  // sends those days to the desk selector, and the server refuses them outright.
  // Only an office with nothing to book (desk_required = 0) is still set this way.
  const setWhereabouts = async (dates, status_code, personId, locationId = null, slotFor = 'full') => {
    setBusy(true);
    try {
      await api.put('whereabouts', {
        dates, status_code, slot: slotFor,
        ...(locationId ? { location_id: locationId } : {}),
        ...(personId && personId !== user?.id ? { person_id: personId } : {}),
      });
      const s = statuses.find(x => x.code === status_code);
      const where = locationId ? locations.find(l => l.id === locationId)?.name : null;
      say('ok', `${where ? `In the office at ${where}` : (s?.label || status_code)} set`
        + (slotFor !== 'full' ? ` for ${slotWord(slotFor)}` : '')
        + ` for ${dates.length} day${dates.length === 1 ? '' : 's'}.`);
      await refresh();
    } catch (e) { say('error', e.message || 'Could not set that'); }
    finally { setBusy(false); }
  };

  // ── CLEARING A DAY CLEARS THE WHOLE DAY ────────────────────────────────────
  //
  // The ✕ on a tile has to mean the same thing wherever it appears: this day now
  // says nothing. On most days that is a statement to delete, but on a day you
  // booked a desk the desk is what is speaking, so clearing has to give the seat
  // up as well or the ✕ would appear to do nothing.
  //
  // Your own seats only. A guest booking is a visitor's seat, held under their
  // name, and saying nothing about where you are is no reason to turn them away.
  //
  // Booking for somebody else reaches their desks and stops there: a day they
  // stated by hand is their own answer about where they are working, which is
  // not desk booking and is not this screen's to rewrite.
  const clearDay = async (dates) => {
    const mine = bookings.filter(b =>
      b.person_id === subject.id && !b.guest_name && dates.includes(b.booking_date));
    setBusy(true);
    try {
      for (const b of mine) await api.delete(`bookings/${b.id}`);
      // Then anything you said by hand. Cancelling a desk already takes back the
      // office statement it made, so this is only ever a statement of your own.
      if (!bookingFor && dates.some(d => myDay(d)?.source === 'explicit')) {
        await api.delete(`whereabouts?dates=${dates.join(',')}`);
      }
      // Name the seats given up. A cleared statement is easily set again; a desk
      // handed back may be gone by the time anybody notices it went.
      const names = [...new Set(mine.map(b => b.resource_name))].join(', ');
      say('ok', names ? `Cleared, and cancelled ${names}.` : 'Cleared.');
      await refresh();
    } catch (e) { say('error', e.message || 'Could not clear that'); }
    finally { setBusy(false); }
  };

  // ── USUAL HOME DAYS ────────────────────────────────────────────────────────
  // Saved as each day is ticked, and answered in the menu where it was ticked
  // rather than in the page banner behind it.
  const toggleHomeDay = async (key) => {
    const ticked = homeDays ? homeDays.split(',') : [];
    const next = WEEKDAYS.slice(0, 5).map(d => d.short.toLowerCase())
      .filter(k => (k === key) !== ticked.includes(k)).join(',');
    setBusy(true);
    setHomeDaysMsg(null);
    try {
      const res = await api.put('whereabouts/usual', { home_days: next || null });
      setHomeDays(res?.home_days || '');
      setHomeDaysMsg({ kind: 'ok', text: res?.home_days ? 'Saved.' : 'Saved. No usual home day.' });
      await loadWeek();
    } catch (e) {
      setHomeDaysMsg({ kind: 'error', text: e.message || 'Could not save that' });
    } finally { setBusy(false); }
  };

  // ── A REFUSED BOOKING IS NOT AN EXCEPTION ──────────────────────────────────
  //
  // POST /bookings answers with a 409 when it booked nothing, and the API client
  // turns any non-2xx into a thrown error. So the one case that has the most to
  // explain — nothing was booked, here is why, per date — landed in the catch
  // block and showed as "HTTP 409", leaving people to scroll back up the screen
  // and work it out themselves.
  //
  // The refusal payload is a result, not a failure, so it is unwrapped back into
  // one here and rendered by the same code as a success.
  const postBooking = async (payload) => {
    try {
      return await api.post('bookings', payload);
    } catch (e) {
      if (e.status === 409 && typeof e.data?.created === 'number') return e.data;
      throw e;
    }
  };

  // ── SWITCHING, NOT FAILING ─────────────────────────────────────────────────
  //
  // `release` is the seats to give up first. One desk per person per slot, so
  // moving from PP-6 to PP-9 means cancelling the first: the server refuses the
  // second while the first is held, and there is no atomic swap to be had.
  //
  // The order is unavoidable but the exposure is not, so if the new seat cannot
  // be taken after all (somebody else got there in between) the old ones are
  // booked straight back, rather than leaving somebody who asked to move with
  // no desk at all.
  const bookDesk = async (desk, release = []) => {
    setPickedDesk(null);
    if (!pickedDates.length) { say('error', 'Pick at least one date first.'); return; }
    setBusy(true);
    try {
      for (const b of release) {
        await api.delete(`bookings/${b.id}`);
      }
      const payload = {
        resource_id: desk.id,
        dates: pickedDates,
        slot,
        ...(guestName.trim() ? { guest_name: guestName.trim() } : {}),
        ...(bookingFor ? { person_id: bookingFor.id } : {}),
        ...(repeatOn && repeatDays.length ? { repeat: { weekdays: repeatDays, until: repeatUntil } } : {}),
      };
      const res = await postBooking(payload);
      const lines = [
        ...(res.conflictDetails || []).map(c =>
          c.conflict?.own_desk
            ? `${fmtShort(c.date)}: ${bookingFor ? `${bookingFor.first_name} already has` : 'you already have'} ${c.conflict.own_desk} for ${slotWord(c.conflict.slot)}`
            : `${fmtShort(c.date)}: taken by ${c.conflict?.person_name || 'someone else'}`),
        ...(res.rejected || []).map(r => `${fmtShort(r.date)}: ${r.reason}`),
      ];
      if (res.created > 0) {
        const days = `${res.created} day${res.created === 1 ? '' : 's'}`;
        const forWhom = bookingFor ? ` for ${bookingFor.name}` : '';
        say(lines.length ? 'warn' : 'ok',
          release.length
            ? `Switched to ${desk.label}${forWhom} for ${days}, and released ${[...new Set(release.map(b => b.resource_name))].join(', ')}.`
            : `Booked ${desk.label}${forWhom} for ${days}.`,
          lines.length ? ['Not booked:', ...lines] : []);
        setGuestName('');
      } else {
        // Put back what was released for a switch that then could not be made.
        // person_id comes off the released row rather than being left to default,
        // or a failed switch made on somebody else's behalf would hand their desk
        // back to whoever pressed the button.
        const restored = [];
        for (const b of release) {
          const back = await api.post('bookings', {
            resource_id: b.resource_id, dates: [b.booking_date], slot: b.slot,
            ...(b.person_id && b.person_id !== user?.id ? { person_id: b.person_id } : {}),
          }).then(r => r.created > 0).catch(() => false);
          if (back) restored.push(b.resource_name);
        }
        // The server's own sentence when it has one: it says what to do about it
        // ("cancel that booking first"), which a bare "could not book" does not.
        say('error', res.error || `Could not book ${desk.label}.`, [
          ...lines,
          ...(restored.length
            ? [`${bookingFor ? `${bookingFor.first_name} still has` : 'You still have'} ${[...new Set(restored)].join(', ')}.`]
            : []),
          ...(release.length && !restored.length
            ? [`The previous desk could not be put back either, so ${bookingFor ? `${bookingFor.name} has` : 'you have'} no desk for those days.`]
            : []),
        ]);
      }
      await refresh();
    } catch (e) { say('error', e.message || 'Booking failed'); }
    finally { setBusy(false); }
  };

  // Release a desk the subject holds across every currently picked date.
  const releaseDesk = async (desk) => {
    const mine = bookings.filter(b =>
      b.resource_id === desk.id && b.person_id === subject.id && !b.guest_name && pickedDates.includes(b.booking_date));
    if (!mine.length) return;
    setBusy(true);
    try {
      for (const b of mine) await api.delete(`bookings/${b.id}`);
      say('ok', `Released ${desk.label}${bookingFor ? ` for ${bookingFor.name}` : ''} for ${mine.length} day${mine.length === 1 ? '' : 's'}.`);
      await refresh();
    } catch (e) { say('error', e.message || 'Could not release that desk'); }
    finally { setBusy(false); }
  };

  // ── DOING THIS WEEK AGAIN ──────────────────────────────────────────────────
  //
  // The desks go in as ordinary bookings, one request per desk and slot with
  // every date on it, so each date is judged on its own and a fortnight where
  // somebody else has taken your Tuesday still books the rest. Whatever could
  // not be had is named, because a silent partial repeat is the one outcome
  // that would leave somebody thinking they had a seat.
  //
  // The days you stated go in afterwards and cannot clash: whereabouts holds one
  // row per person per day and the write replaces it.
  const repeatThisWeek = async () => {
    const weeks = Math.min(Math.max(1, Number(repeatWeeks) || 1), maxRepeatWeeks);
    const { desks: runs, statements } = expandWeek(weekPlan, weeks, { after: todayIso() });
    if (!runs.length && !statements.length) { say('error', 'Nothing on this week to repeat.'); return; }
    const nameOf = Object.fromEntries(
      weekPlan.flatMap(d => d.seats).map(s => [s.resource_id, s.resource_name]));

    setRepeatWeekOpen(false);
    setBusy(true);
    try {
      let booked = 0;
      let stated = 0;
      const lines = [];
      for (const run of runs) {
        const label = nameOf[run.resource_id] || 'that desk';
        try {
          const res = await postBooking({
            resource_id: run.resource_id, dates: run.dates, slot: run.slot,
            ...(bookingFor ? { person_id: bookingFor.id } : {}),
          });
          booked += res.created || 0;
          for (const c of res.conflictDetails || []) {
            const mine = bookings.some(b => b.person_id === subject.id && !b.guest_name
              && b.resource_id === run.resource_id && b.booking_date === c.date
              && slotsOverlap(b.slot, run.slot));
            lines.push(`${fmtShort(c.date)}: ${skipReason(c.conflict, { label, mine })}`);
          }
          for (const r of res.rejected || []) lines.push(`${fmtShort(r.date)}: ${label} — ${r.reason}`);
        } catch (e) {
          lines.push(`${label}: ${e.message || 'could not be booked'}`);
        }
      }
      // The days somebody stated by hand are repeated only when the week is your
      // own. Booking for a colleague covers their desks and nothing else, so a
      // repeat on their behalf carries the seats and leaves what they said about
      // working from home to them.
      for (const s of (bookingFor ? [] : statements)) {
        try {
          await api.put('whereabouts', {
            dates: s.dates,
            status_code: s.status_code,
            slot: s.slot || 'full',
            ...(s.location_id ? { location_id: s.location_id } : {}),
          });
          stated += s.dates.length;
        } catch (e) {
          lines.push(`${s.status_code}: ${e.message || 'could not be set'}`);
        }
      }
      const parts = [
        booked ? `${booked} desk booking${booked === 1 ? '' : 's'}` : null,
        stated ? `${stated} day${stated === 1 ? '' : 's'} set` : null,
      ].filter(Boolean);
      say(booked || stated ? (lines.length ? 'warn' : 'ok') : 'error',
        parts.length
          ? `Repeated for the next ${weeks} week${weeks === 1 ? '' : 's'}: ${parts.join(' and ')}.`
          : 'Nothing could be repeated.',
        lines.length ? ['Not booked:', ...lines] : []);
      await refresh();
    } catch (e) { say('error', e.message || 'Could not repeat that week'); }
    finally { setBusy(false); }
  };

  const cancelBooking = async (b, wholeSeries = false) => {
    setBusy(true);
    try {
      const res = await api.delete(`bookings/${b.id}${wholeSeries ? '?series=1' : ''}`);
      say('ok', res.cancelled > 1 ? `Cancelled ${res.cancelled} bookings.` : 'Booking cancelled.');
      await refresh();
    } catch (e) { say('error', e.message || 'Could not cancel'); }
    finally { setBusy(false); }
  };

  const toggleFavourite = async (desk) => {
    const existing = favourites.find(f => f.desk_id === desk.id);
    try {
      if (existing) await api.delete(`desk-favourites/${desk.id}`);
      else await api.post('desk-favourites', { desk_id: desk.id });
      setFavourites(await api.get('desk-favourites'));
    } catch (e) { say('error', e.message || 'Could not update favourites'); }
  };

  const bookVan = async () => {
    if (!selectedVan) { say('error', 'Select a van first.'); return; }
    setBusy(true);
    try {
      const res = await postBooking({ resource_id: selectedVan, dates: pickedDates, slot, notes: vanNotes || undefined });
      if (res.created > 0) { say('ok', `Van booked for ${res.created} day${res.created === 1 ? '' : 's'}.`); setVanNotes(''); }
      else say('error', res.error || 'That van is already booked for that slot.',
        (res.conflictDetails || []).map(c => `${fmtShort(c.date)}: ${c.conflict?.person_name || 'taken'}`));
      await refresh();
    } catch (e) { say('error', e.message || 'Booking failed'); }
    finally { setBusy(false); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const currentLocation = locations.find(l => l.id === selectedLocation);
  const desks = currentLocation?.desks || [];
  const myDay = (d) => planner.days?.[d]?.[subject.id] || null;

  const myBookings = useMemo(() => bookings.filter(b => b.person_id === subject.id), [bookings, subject.id]);

  // ── THE WEEK ON SCREEN, AS SOMETHING THAT COULD BE DONE AGAIN ──────────────
  //
  // A week's plan is both its desks and the days you said where you were, so
  // both are collected: repeating a week that comes back without its Wednesday
  // at home has not repeated the week.
  //
  // Two kinds of day are left out. Leave comes from an approved request and
  // there is nothing this screen could do about next month's. A day whose
  // answer came from the desk booking itself is already covered by repeating
  // the desk, which states the office again on the far side.
  const weekPlan = useMemo(() => visibleDates.map(d => {
    const seats = bookings
      .filter(b => b.person_id === subject.id && !b.guest_name
        && b.booking_date === d && b.resource_type === 'desk')
      .map(b => ({ resource_id: b.resource_id, slot: b.slot, resource_name: b.resource_name }));
    const w = planner.days?.[d]?.[subject.id] || null;
    // The office an "in the office" day names, so the repeat says which one
    // rather than dropping back to the bare statement. The planner gives the
    // name rather than the id, so it is matched back against the office list
    // and simply left off if it cannot be.
    const stated = w && w.source === 'explicit'
      ? {
          status_code: w.code,
          label: w.label,
          location_id: locations.find(l => l.name === String(w.detail || '').split(' · ')[0])?.id || null,
          // The planner reports the slot in display form ('AM'); the API takes
          // it lower-case, the same as bookings.
          slot: w.slot ? String(w.slot).toLowerCase() : 'full',
        }
      : null;
    return { date: d, seats, stated };
  }).filter(day => day.seats.length || day.stated),
  [visibleDates, bookings, planner, locations, subject.id]);

  // How many times over it could be repeated: whole weeks, ending inside the
  // booking window. The same restriction the desk booking itself applies, asked
  // up front so nobody picks a number that is going to be half refused.
  const maxRepeatWeeks = useMemo(
    () => weeksAvailable(
      weekPlan.length ? weekPlan[weekPlan.length - 1].date : visibleDates[visibleDates.length - 1],
      maxBookDate),
    [weekPlan, visibleDates, maxBookDate],
  );

  // Two bookings clash unless one is AM and the other PM.
  const slotsOverlap = (a, b) => a === 'full' || b === 'full' || a === b;

  // ── WHAT YOU ALREADY HOLD ──────────────────────────────────────────────────
  //
  // The desks this person would be giving up by booking this one: their own desk
  // on a picked date, in a slot that overlaps. Exactly the rule the server
  // applies, so the screen can offer the switch up front instead of asking for a
  // confirmation it knows will be refused.
  //
  // A guest seat is exempt at both ends: seating a visitor says nothing about
  // where the host sits, and the host's own desk is not in the way of it.
  const heldAgainst = (desk) => {
    if (!desk || guestName.trim()) return [];
    return bookings.filter(b =>
      b.person_id === subject.id && !b.guest_name && b.resource_type === 'desk'
      && b.resource_id !== desk.id
      && pickedDates.includes(b.booking_date)
      && slotsOverlap(b.slot, slot));
  };

  const deskState = (desk) => {
    // A reserved desk opens up while its owner cannot be at it: on leave, having
    // said they are somewhere else, or having booked a different seat. So whether
    // it is locked depends on the date AND the slot, not just on the desk. The
    // server decides all of that; this reads its answer.
    const freeSlots = released[desk.id] || {};
    const releasedOnAll = pickedDates.length > 0
      && pickedDates.every(d => freeSlots[d] === 'full' || freeSlots[d] === slot);
    // Your own reserved desk is always yours to take. Everyone else waits for it
    // to be released. Read against the subject, so booking for Rachel opens
    // Rachel's own desk to her and not to whoever is pressing the button.
    const mineByAssignment = !!desk.assigned_person_id && desk.assigned_person_id === subject.id;

    if (desk.status === 'reserved' && !releasedOnAll && !mineByAssignment) {
      return { status: 'reserved', note: desk.status_note };
    }
    if (desk.status === 'blocked') return { status: 'blocked' };
    if (!desk.is_active && !releasedOnAll && !mineByAssignment && desk.status !== 'bookable') return { status: 'blocked' };
    // A desk only counts as available if it is free on EVERY picked date,
    // otherwise booking would silently drop the dates that clash.
    const on = bookings.filter(b =>
      b.resource_id === desk.id && pickedDates.includes(b.booking_date) && slotsOverlap(b.slot, slot));
    const others = on.filter(b => b.person_id !== subject.id || b.guest_name);
    if (others.length) {
      return {
        status: 'taken',
        person: others[0].guest_name || others[0].person_name,
        person_id: others[0].guest_name ? null : others[0].person_id,
        person_color: others[0].avatar_color,
        // What they do, for the plan's hover card. Two initials on a seat are no use unless
        // something will tell you whose they are.
        person_title: others[0].guest_name ? 'Visitor' : (titleOf[others[0].person_id] || null),
        // Half-days matter: an AM booking leaves the afternoon free, and the
        // plan cannot show that without the slot.
        slot: others.length === 1 ? others[0].slot : 'full',
      };
    }
    if (on.length) return { status: 'mine', slot: on.length === 1 ? on[0].slot : 'full' };
    // A seat that is only free because its owner is away is not the same thing as
    // a seat that is free every day, and the hover card says so.
    return {
      status: 'available',
      released: desk.status === 'reserved' && releasedOnAll,
      // Yours by assignment rather than free to all, which the plan says differently.
      yours: desk.status === 'reserved' && mineByAssignment && !releasedOnAll,
    };
  };

  // Everyone booked into the currently selected office and day, for the list
  // under the plan. Sorted by desk so it reads in the same order as the map.
  const seatedHere = useMemo(() => {
    const day = pickedDates[0];
    const deskIds = new Set(desks.map(d => d.id));
    return bookings
      .filter(b => b.booking_date === day && deskIds.has(b.resource_id))
      .sort((a, b) => String(a.resource_name).localeCompare(String(b.resource_name), undefined, { numeric: true }));
  }, [bookings, desks, pickedDates]);

  // Desks in the current office that are open only because their owner is not at
  // them: on leave, somewhere else, or at a different seat.
  const releasedHere = useMemo(
    () => desks.filter(d => pickedDates.some(x => (released[d.id] || {})[x])),
    [desks, released, pickedDates],
  );

  // ── WHAT THE PICKER NEEDS TO KNOW ──────────────────────────────────────────
  //
  // Three questions, in the order somebody has them: which office, is there room,
  // and where is my team sitting. The plan answers the third beautifully and the
  // first two not at all, which is why they are computed here and shown above it.

  // My own seats on the day in view, so the screen can say "you are on PP-4"
  // instead of leaving somebody to find their own desk on the plan.
  // person id → job title, from the planner, which already carries everybody for the week.
  const titleOf = useMemo(() => Object.fromEntries(
    (planner.people || []).map(p => [p.id, p.designation]).filter(([, t]) => t)
  ), [planner.people]);

  const mySeatsToday = useMemo(
    () => bookings.filter(b => b.person_id === subject.id && !b.guest_name && b.booking_date === pickedDates[0]),
    [bookings, subject.id, pickedDates],
  );

  // The people in my team, so their desks can be pointed at. Read from the
  // planner, which already carries the whole company for the week. Booking for
  // somebody else follows THEIR team: "sit near the people I work with" is a
  // decision being made on their behalf, not on yours.
  const myTeamIds = useMemo(() => {
    const me = planner.people?.find(p => p.id === subject.id);
    if (!me?.team_id) return new Set();
    return new Set((planner.people || []).filter(p => p.team_id === me.team_id && p.id !== subject.id).map(p => p.id));
  }, [planner.people, subject.id]);

  // A count per office, so the choice of office is made on the numbers rather
  // than on the name. Every office, not just the selected one — that is the whole
  // point of showing it before you have picked.
  const officeStats = useMemo(() => {
    const day = pickedDates[0];
    const out = {};
    for (const loc of locations) {
      const st = { free: 0, bookable: 0, mine: 0, team: 0 };
      for (const d of loc.desks || []) {
        const s = deskState(d);
        if (['available', 'taken', 'mine'].includes(s.status)) st.bookable++;
        if (s.status === 'available') st.free++;
        if (s.status === 'mine') st.mine++;
      }
      const deskIds = new Set((loc.desks || []).map(d => d.id));
      st.team = bookings.filter(b => b.booking_date === day && deskIds.has(b.resource_id)
        && myTeamIds.has(b.person_id)).length;
      out[loc.id] = st;
    }
    return out;
    // deskState closes over bookings, released, slot and pickedDates, all listed.
  }, [locations, bookings, released, slot, pickedDates, myTeamIds]);

  // Teammates' desks in this office, haloed on the plan. "Sit near the people I
  // work with" is the actual decision most people are making.
  const teamDeskIds = useMemo(() => {
    const day = pickedDates[0];
    const deskIds = new Set(desks.map(d => d.id));
    return new Set(bookings
      .filter(b => b.booking_date === day && deskIds.has(b.resource_id) && myTeamIds.has(b.person_id))
      .map(b => b.resource_id));
  }, [bookings, desks, pickedDates, myTeamIds]);

  // The named areas of this office, with their tint from the plan so a filter
  // chip and the region it selects are the same colour.
  const zoneAreas = useMemo(() => {
    const zs = parseZones(currentLocation).filter(z => z.label && !['wall', 'door'].includes(z.type));
    return zs;
  }, [currentLocation]);

  // Which area a desk sits in, by geometry: a desk is in the smallest labelled
  // zone that contains its centre. Smallest, because zones nest — a pod inside a
  // room inside a wing — and the innermost is the useful answer.
  const zoneOfDesk = useCallback((desk) => {
    if (desk.pos_x == null || desk.pos_y == null) return null;
    const hits = zoneAreas.filter(z =>
      desk.pos_x >= z.x && desk.pos_x <= z.x + z.w && desk.pos_y >= z.y && desk.pos_y <= z.y + z.h);
    if (!hits.length) return null;
    return hits.sort((a, b) => (a.w * a.h) - (b.w * b.h))[0].label;
  }, [zoneAreas]);

  const zoneFree = useMemo(() => {
    const out = {};
    for (const d of desks) {
      const z = zoneOfDesk(d);
      if (!z) continue;
      if (deskState(d).status === 'available') out[z] = (out[z] || 0) + 1;
    }
    return out;
  }, [desks, zoneOfDesk, bookings, released, slot, pickedDates, subject.id]);

  // Only the areas you could actually sit in get a filter chip. A plan labels
  // the WCs, the kitchen and the printer nook as well as the desk pods, and
  // "MALE WC · 0 free" is a filter that can never return anything. Counted over
  // every desk rather than the free ones, so a fully booked pod keeps its chip.
  const zoneChips = useMemo(() => {
    const withDesks = new Set(desks.map(zoneOfDesk).filter(Boolean));
    return zoneAreas.filter(z => withDesks.has(z.label));
  }, [desks, zoneAreas, zoneOfDesk]);

  // What the plan draws: the area filter and "free only" applied. Filtering the
  // PLAN rather than only the list is deliberate — hiding what you cannot have is
  // most of what makes a 39-desk office readable.
  const visibleDesks = useMemo(() => desks.filter(d => {
    if (zoneFilter && zoneOfDesk(d) !== zoneFilter) return false;
    if (freeOnly && !['available', 'mine'].includes(deskState(d).status)) return false;
    return true;
  }), [desks, zoneFilter, freeOnly, zoneOfDesk, bookings, released, slot, pickedDates, subject.id]);

  // The list view: the same desks, grouped by area and sorted the way a person
  // reads a desk label (PP-2 before PP-10).
  const deskGroups = useMemo(() => {
    const groups = new Map();
    for (const d of visibleDesks) {
      const key = zoneOfDesk(d) || 'Elsewhere in the office';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    }
    return [...groups.entries()]
      .map(([label, list]) => ({
        label,
        desks: list.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true })),
        free: list.filter(d => deskState(d).status === 'available').length,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleDesks, zoneOfDesk, bookings, released, slot, pickedDates, subject.id]);

  // The office status in the vocabulary, whatever it is called. Read rather than assumed,
  // because the status list is admin-managed and an office can add its own.
  const officeStatus = useMemo(
    () => statuses.find(s => s.code === 'office') || statuses.find(s => /office/i.test(s.label || '')),
    [statuses],
  );
  // Which days an answer given on a tile applies to. Normally just that tile: a
  // day is set from its own tile, and clicking one must never quietly rewrite the
  // others. With "select several days" on and more than one picked, a tile inside
  // that selection sets the whole selection, which is what the toggle is for.
  const whereDates = (d) =>
    multiDay && pickedDates.length > 1 && pickedDates.includes(d) ? pickedDates : [d];

  // Days you already have something booked, dotted on the month calendar.
  const markedDays = useMemo(() => {
    const out = {};
    for (const b of myBookings) out[b.booking_date] = true;
    return out;
  }, [myBookings]);

  // Picking a day. In single-day mode the day you clicked replaces whatever was
  // selected; in multi-day mode it toggles, and the last remaining day cannot be
  // unpicked because every panel below is written for at least one date.
  const pickDate = (d) => setPickedDates(prev => {
    if (!multiDay) return [d];
    if (!prev.includes(d)) return [...prev, d].sort();
    return prev.length > 1 ? prev.filter(x => x !== d) : prev;
  });

  // Leaving multi-day mode keeps the first selected day, so the screen carries on
  // showing something rather than emptying.
  const setMultiDayMode = (on) => {
    setMultiDay(on);
    if (!on) setPickedDates(prev => prev.length > 1 ? [prev[0]] : prev);
  };

  const multiDayToggle = (
    <label data-tour="multi-day" className={`${T.body} inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1.5 ${
      multiDay ? 'border-navy bg-navy/5 text-navy font-semibold' : 'border-gray-300 bg-white text-gray-700'}`}>
      <input type="checkbox" className="w-3.5 h-3.5" checked={multiDay}
        onChange={e => setMultiDayMode(e.target.checked)} />
      Select several days
    </label>
  );

  // Beside it, because they are the same kind of control: one plans several days
  // at once, the other plans several weeks at once.
  const repeatWeekButton = (
    <Btn
      data-tour="repeat-week"
      onClick={() => { setRepeatWeeks(Math.min(1, maxRepeatWeeks) || 1); setRepeatWeekOpen(true); }}
      disabled={busy || !weekPlan.length || maxRepeatWeeks < 1}
      title={!weekPlan.length
        ? 'Nothing booked or set this week to repeat'
        : maxRepeatWeeks < 1
          ? 'Next week is past the end of the booking window'
          : 'Book this week again for the weeks ahead'}
    >
      Repeat week
    </Btn>
  );

  const bannerStyle = {
    ok:    'bg-emerald-50 border-emerald-300 text-emerald-900',
    warn:  'bg-amber-50 border-amber-300 text-amber-900',
    error: 'bg-red-50 border-red-300 text-red-900',
  };

  if (loading) {
    return <div className={`${T.body} text-gray-500 p-10 text-center`}>Loading bookings…</div>;
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          {/* Replay the walkthrough. Always available, so "it only shows once"
              never strands somebody who skipped it and regrets it.

              A circled ? against the heading, which is where people look for
              "explain this screen". It sat in the tab row as a "? Tour" button
              before, and read as a fourth tab. */}
          <div className="flex items-center gap-2">
            <h2 className={`${T.page} text-navy`}>Bookings</h2>
            <button
              type="button"
              onClick={tour.start}
              aria-label="Replay the guided tour of this page"
              title="Replay the guided tour of this page"
              className={`w-[23px] h-[23px] shrink-0 rounded-full border border-gray-300 bg-white
                text-gray-500 ${T.strong} leading-none flex items-center justify-center
                transition-colors hover:border-navy hover:text-navy hover:bg-gray-50`}
            >
              ?
            </button>
          </div>
          <p className={`${T.meta} text-gray-500 mt-0.5`}>
            Plan your week, book a desk or van, and see who is in.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          {/* ── BOOKING FOR SOMEBODY ELSE ──
              Two steps: choose a name, then press the button. Choosing on its own
              changes nothing, so a stray click on a dropdown cannot put a
              colleague in a seat. Desks only, so it is not offered on the vans or
              team tabs — and the mode ends if you leave this one. */}
          {canBookForOthers && tab === 'week' && (
            <div className="flex gap-1.5 items-center" data-tour="booking-for">
              <select
                value={bookingForDraft}
                onChange={e => setBookingForDraft(e.target.value)}
                disabled={busy || !bookableFor.length}
                aria-label="Choose somebody to book a desk for"
                className={`${T.body} border border-gray-300 rounded-md px-2.5 py-2 bg-white max-w-[12rem]`}
              >
                <option value="">Book for someone…</option>
                {bookableFor.map(p => (
                  <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                ))}
              </select>
              <Btn
                active={!!bookingForDraft}
                disabled={!bookingForDraft || busy}
                onClick={() => startBookingFor(bookingForDraft)}
                title={bookingForDraft
                  ? 'Book desks for this person as if they were booking themselves'
                  : 'Choose somebody first'}
              >
                Start
              </Btn>
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap" data-tour="tabs">
            {TABS.map(t => (
              <Btn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</Btn>
            ))}
          </div>
        </div>
      </div>

      {/* The mode, while it is on. Deliberately hard to miss and impossible to
          confuse with your own week: every panel below this line is about the
          named person, so the page has to say so at the top of it. */}
      {bookingFor && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center gap-3 flex-wrap">
          <Avatar as="span" personId={bookingFor.id} className="w-8 h-8 rounded-full bg-white border border-amber-300 text-amber-900 flex items-center justify-center font-bold shrink-0">
            {bookingFor.name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
          </Avatar>
          <div className="min-w-[12rem]">
            <div className={`${T.strong} text-amber-900`}>Booking for {bookingFor.name}</div>
            <div className={`${T.meta} text-amber-800`}>
              Everything on this page is theirs. They will be emailed anything you book or cancel.
            </div>
          </div>
          <Btn className="ml-auto" onClick={stopBookingFor} disabled={busy}>
            Book for myself again
          </Btn>
        </div>
      )}

      {/* A load that failed. Without this the page drew whatever it had, so a
          refused request looked like "No vans available" and, worse, a desk or
          van whose bookings never arrived looked free. */}
      {failedParts.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-900 p-3 flex items-center gap-3 flex-wrap">
          <div className="min-w-[12rem] flex-1">
            <div className={T.strong}>Part of this page didn't load</div>
            <div className={T.meta}>
              Couldn't load {failedParts.length > 1 ? `${failedParts.slice(0, -1).join(', ')} and ${failedParts[failedParts.length - 1]}` : failedParts[0]}.
              {' '}What's shown may be incomplete, and a desk or van can look free when it isn't.
            </div>
          </div>
          <Btn onClick={retryLoad} disabled={retrying}>{retrying ? 'Trying again…' : 'Try again'}</Btn>
        </div>
      )}

      {/* Persistent result panel */}
      {banner && (
        <div ref={bannerRef} className={`mb-4 rounded-lg border p-3 ${bannerStyle[banner.kind] || bannerStyle.ok}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={`${T.strong}`}>{banner.title}</div>
              {banner.lines?.length > 0 && (
                <ul className={`${T.meta} mt-1.5 space-y-0.5`}>
                  {banner.lines.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              )}
            </div>
            <button type="button" onClick={clearBanner} className={`${T.body} opacity-60 hover:opacity-100 px-1`} title="Dismiss">✕</button>
          </div>
        </div>
      )}

      {/* Week navigation, laid out like Team Today: arrows and the date on the
          left, view switch in the middle, Today on the right. */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setWeekStart(w => addDays(w, -7))} aria-label="Previous week"
            className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 leading-none">‹</button>
          <button type="button" onClick={() => setWeekStart(w => addDays(w, 7))} aria-label="Next week"
            className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 leading-none">›</button>
        </div>
        <span className={`${T.title} text-gray-800`}>
          {fmtRange(visibleDates[0], visibleDates[visibleDates.length - 1])}
        </span>
        {/* Kept beside the dates rather than pushed to the far edge: these are
            the controls for the week you are looking at, and on a wide screen
            ml-auto left a metre of empty header between them and it. */}
        <Btn onClick={() => { setWeekStart(startOfWeek(todayIso())); setPickedDates([todayIso()]); }}>
          Today
        </Btn>
        {tab !== 'team' && multiDayToggle}
        {tab === 'week' && repeatWeekButton}
      </div>

      {/* ── WEEK: the booking screen ──
          Laid out like Team Today: days across the top, your row of tiles under
          them, the desk picker below that, and the month calendar down the right.
          Booking used to live on a separate tab, which meant picking a day here
          and then finding it again over there. */}
      {tab === 'week' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4 items-start">
          <div className="space-y-4">
            {/* Day columns */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-tour="week-strip">
              <div className="min-w-[560px]">
                <div className="grid border-b border-gray-200 bg-gray-50"
                  style={{ gridTemplateColumns: `repeat(${visibleDates.length}, minmax(0, 1fr))` }}>
                  {visibleDates.map(d => {
                    const isToday = d === todayIso();
                    const locked = beyondWindow(d);
                    return (
                      <div key={d} className={`px-2 py-2 text-center border-l first:border-l-0 border-gray-200
                        ${isWeekend(d) ? 'bg-gray-100' : ''} ${locked ? 'opacity-40' : ''}`}>
                        <div className={`${T.label} ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>{fmtDay(d)}</div>
                        <div className={`${T.title} ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>{d.slice(8)}</div>
                      </div>
                    );
                  })}
                </div>

                {/* ── YOUR ROW, AND WHERE YOU ARE ──
                    One tile per day: what you are doing, and the desk if you have
                    one. Clicking a tile selects that day below; the icon slot is a
                    control of its own — a + on a day with nothing set — and opens
                    the short "where are you" menu for that day. A day carrying your
                    own answer gets a clear control in its corner.

                    That replaced a standing panel under the strip. Booking a desk
                    already states which office you are at, which covers most days,
                    so the panel was a permanent fixture for an occasional answer. */}
                <div className="grid" data-tour="whereabouts" style={{ gridTemplateColumns: `repeat(${visibleDates.length}, minmax(0, 1fr))` }}>
                  {visibleDates.map(d => {
                    const w = myDay(d);
                    const c = w ? tone(w.tone) : null;
                    const picked = pickedDates.includes(d);
                    const locked = beyondWindow(d);
                    // Anything on the day can be cleared, a desk booking included:
                    // clearDay gives the seat up as part of it. Leave is the one
                    // exception, because it comes from an approved leave request
                    // and a ✕ here could not do anything about that.
                    //
                    // Booking for somebody else clears their desks only, so the ✕
                    // appears on their day only when a desk is what is speaking.
                    // Their own "working from home" is not this screen's to undo.
                    //
                    // A usual home day has nothing to clear either: it is what the
                    // day is when nothing else is said, so answering the day
                    // differently is how it changes. Nor does a working away day,
                    // which the PLOW's planner set and only the PLOW can change.
                    const clearable = !!w && w.source !== 'leave' && w.source !== 'usual' && w.source !== 'plow'
                      && (!bookingFor || w.source === 'desk');
                    return (
                      <div
                        key={d}
                        className={`relative min-h-[84px] p-1.5 border-l first:border-l-0 border-b border-gray-200 text-center
                          transition-colors
                          ${locked ? 'bg-gray-50 opacity-40'
                            : picked ? 'ring-2 ring-inset ring-navy bg-navy/5'
                            : 'hover:bg-gray-50'}`}
                      >
                        {/* Selecting the day is the whole tile. An overlay button
                            rather than a wrapper, because the icon slot and the
                            clear control are buttons in their own right and a
                            button cannot contain a button. */}
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => pickDate(d)}
                          aria-label={`Select ${fmtLong(d)}`}
                          title={locked ? `Bookable from ${maxBookDate} onwards only` : [w?.label, w?.detail].filter(Boolean).join(' · ')}
                          className="absolute inset-0 w-full h-full disabled:cursor-not-allowed"
                        />
                        <button
                          type="button"
                          disabled={locked || !!bookingFor}
                          onClick={() => { setWhereSlot('full'); setWhereMenu(d); }}
                          aria-label={bookingFor
                            ? `Where ${bookingFor.name} is working on ${fmtLong(d)}`
                            : `Set where you are on ${fmtLong(d)}`}
                          title={bookingFor
                            ? `Only ${bookingFor.first_name} can say where they are working. You can book them a desk.`
                            : w ? 'Change where you are on this day' : 'Say where you are on this day'}
                          className={`relative mx-auto w-11 h-11 rounded-lg border flex items-center justify-center text-[19px]
                            transition-colors disabled:cursor-not-allowed
                            ${c ? `${c.bg} ${c.border}`
                              : 'bg-white border-gray-300 border-dashed text-gray-400 hover:border-navy hover:text-navy'}`}
                        >
                          {/* A split day shows both halves, morning first. */}
                          {w?.halves
                            ? <span className="text-[13px] leading-none whitespace-nowrap">
                                {w.halves.am?.icon || '·'}<span className="text-gray-300 text-[10px]">/</span>{w.halves.pm?.icon || '·'}
                              </span>
                            : (w ? (w.icon || '·') : '+')}
                        </button>
                        {/* Above the overlay so it is legible, but not in the way
                            of clicking the tile to select the day. */}
                        <div className={`${T.meta} mt-1.5 truncate relative pointer-events-none ${c ? c.text : 'text-gray-400'}`}>
                          {w?.halves
                            ? `AM ${w.halves.am?.short_label || '—'} · PM ${w.halves.pm?.short_label || '—'}`
                            : w ? (w.detail || w.short_label || w.label) : 'Not set'}
                        </div>
                        {clearable && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => clearDay(whereDates(d))}
                            aria-label={`Clear ${fmtLong(d)}`}
                            title={w?.source === 'desk' || w?.source === 'van'
                              ? `Clear this day and cancel ${w.detail || 'your booking'}`
                              : 'Clear this day'}
                            className={`absolute top-1 right-1 w-5 h-5 rounded-full border border-gray-300 bg-white
                              text-gray-500 leading-none ${T.meta} hover:bg-gray-100 hover:text-gray-800
                              disabled:opacity-50`}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── PICK A DESK ────────────────────────────────────────────────
                Rebuilt around the three questions somebody actually has, in the
                order they have them: which office, is there room, and where is
                my team sitting. The plan used to be the whole answer and it was
                the last of the three. */}
            <div ref={deskPanelRef} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-3.5 py-3 border-b border-gray-200 bg-gradient-to-b from-slate-50 to-white">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className={`${T.title} text-gray-900`}>
                    Pick a desk
                    <span className={`${T.meta} font-medium text-gray-500 ml-1.5`}>
                      {pickedDates.length === 1 ? `for ${fmtLong(pickedDates[0])}` : `for ${pickedDates.length} selected days`}
                    </span>
                  </span>
                  {mySeatsToday.length > 0 && (
                    <span className={`${T.meta} font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1`}>
                      {bookingFor ? `${bookingFor.first_name} is on` : 'You are on'} {mySeatsToday.map(b => b.resource_name).join(', ')}
                    </span>
                  )}
                </div>

                {/* ── WHICH OFFICE ──
                    A card each, with the free count on it. Naming the offices in
                    a row of plain buttons made them look interchangeable; they
                    are not, and the number is what decides which one you pick. */}
                <div className="mt-3 grid gap-2" data-tour="offices" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(140px, 1fr))` }}>
                  {deskOffices.map(loc => {
                    const st = officeStats[loc.id] || { free: 0, bookable: 0, mine: 0, team: 0 };
                    const on = selectedLocation === loc.id;
                    const full = st.bookable > 0 && st.free === 0;
                    return (
                      <button key={loc.id} type="button" onClick={() => setSelectedLocation(loc.id)}
                        className={`text-left rounded-lg border p-2.5 transition-all ${
                          on ? 'border-navy bg-navy text-white shadow-md ring-2 ring-navy/20'
                             : 'border-gray-200 bg-white hover:border-navy/40 hover:shadow-sm'}`}>
                        <div className="flex items-center justify-between gap-1">
                          <span className={`${T.strong} ${on ? 'text-white' : 'text-gray-900'}`}>{loc.name}</span>
                          {st.mine > 0 && <span className={`${T.meta} ${on ? 'text-teal-200' : 'text-teal-700'}`}>✓ {them}</span>}
                        </div>
                        <div className={`mt-1 ${T.figure} ${
                          on ? (full ? 'text-red-300' : 'text-emerald-300')
                             : (full ? 'text-red-600' : 'text-emerald-600')}`}>
                          {st.free}
                          <span className={`${T.meta} font-semibold ${on ? 'text-white/60' : 'text-gray-400'}`}> / {st.bookable} free</span>
                        </div>
                        {/* Where your team is, which is the other reason to choose
                            one office over another. */}
                        <div className={`${T.meta} ${on ? 'text-white/70' : 'text-gray-500'}`}>
                          {st.bookable === 0 ? 'no desks set up'
                            : st.team > 0 ? `${st.team} of ${theirs} team in`
                            : full ? 'full' : `no-one from ${theirs} team yet`}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-3.5 space-y-3">
                {/* ── CONTROLS ──
                    Half-days, then the two things that make a dense plan
                    usable: which part of the office, and hiding what you cannot
                    have. Then a list, for a phone or a screen reader. */}
                <div className="flex items-center gap-2 flex-wrap" data-tour="desk-controls">
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                    {SLOTS.map(sl => (
                      <button key={sl.value} type="button" onClick={() => setSlot(sl.value)}
                        className={`px-3 py-1.5 ${T.body} font-semibold border-l first:border-l-0 border-gray-300 ${
                          slot === sl.value ? 'bg-navy text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {sl.label}
                      </button>
                    ))}
                  </div>

                  <label className={`${T.body} inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1.5 ${
                    freeOnly ? 'border-emerald-400 bg-emerald-50 text-emerald-900 font-semibold' : 'border-gray-300 bg-white text-gray-700'}`}>
                    <input type="checkbox" className="w-3.5 h-3.5" checked={freeOnly} onChange={e => setFreeOnly(e.target.checked)} />
                    Free only
                  </label>

                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden ml-auto">
                    {[['map', 'Plan'], ['list', 'List']].map(([id, label]) => (
                      <button key={id} type="button" onClick={() => setDeskView(id)}
                        className={`px-3 py-1.5 ${T.body} font-semibold border-l first:border-l-0 border-gray-300 ${
                          deskView === id ? 'bg-navy text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Neighbourhoods, if this office has any. Tinted to match the
                    plan, so the chip and the area on it are the same colour. */}
                {zoneChips.length > 1 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`${T.label} text-gray-500`}>Area</span>
                    <button type="button" onClick={() => setZoneFilter('')}
                      className={`px-2.5 py-1 rounded-full border ${T.meta} font-semibold ${
                        zoneFilter === '' ? 'bg-navy text-white border-navy' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      Anywhere
                    </button>
                    {zoneChips.map(z => {
                      const on = zoneFilter === z.label;
                      const n = zoneFree[z.label] || 0;
                      return (
                        <button key={z.id} type="button" onClick={() => setZoneFilter(on ? '' : z.label)}
                          className={`px-2.5 py-1 rounded-full border ${T.meta} font-semibold ${
                            on ? 'text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                          style={on ? { background: accent, borderColor: accent } : undefined}>
                          {z.label} · {n} free
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Your starred desks, and only ever yours: favourites are a
                    personal shortcut and there is no way to know somebody
                    else's, so they are not offered while the mode is on rather
                    than sitting under a heading that would be a lie. */}
                {favourites.length > 0 && !bookingFor && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${T.label} text-gray-500`}>Your desks</span>
                    {favourites.map(f => {
                      const desk = desks.find(d => d.id === f.desk_id);
                      const st = desk ? deskState(desk) : null;
                      const free = st?.status === 'available';
                      return (
                        <button key={f.id} type="button" disabled={busy || !desk || !free}
                          onClick={() => desk && setConfirmDesk(desk)}
                          title={!desk ? 'Not at this office' : free ? 'Book it' : 'Not free on these dates'}
                          className={`px-2.5 py-1.5 rounded-lg border ${T.body} font-semibold disabled:opacity-40
                            bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100`}>
                          ★ {f.label}{desk && !free ? ' — taken' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Desks freed because their owner is away. Called out rather than
                    quietly turning green, so nobody wonders why a locked seat
                    suddenly opened. */}
                {releasedHere.length > 0 && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5">
                    <div className={`${T.body} font-semibold text-emerald-900`}>Free — their owner is not at them today</div>
                    <div className={`${T.meta} text-emerald-800 mt-1`}>
                      {releasedHere.map(d => `${d.label}${releaseOwners[d.id] ? ` (${releaseOwners[d.id]})` : ''}`).join(' · ')}
                    </div>
                  </div>
                )}

                {!currentLocation ? (
                  <div className={`${T.body} text-gray-500 text-center py-10`}>Select an office</div>
                ) : desks.length === 0 ? (
                  <div className={`${T.body} text-gray-500 text-center py-10`}>No desks set up for {currentLocation.name}</div>
                ) : (
                  <>
                    {deskView === 'map' ? (
                      // ── TAP A SEAT, GET A CARD ON IT ──
                      // The reference does not open a dialog when you tap a desk: a small card
                      // appears ON the seat with "Book AM" and "Book PM" on it. That is better
                      // than a modal, because the plan stays visible behind your decision —
                      // you can see what is next to the seat while you choose it.
                      <div className="relative" data-tour="floor-plan">
                        <FloorPlanMap
                          title={currentLocation.name}
                          desks={visibleDesks}
                          getState={deskState}
                          selectedId={pickedDesk?.id || ''}
                          highlightIds={teamDeskIds}
                          onAnchor={setDeskAnchor}
                          onSelect={(desk, from) => {
                            const st = deskState(desk);
                            if (st.status === 'mine') { releaseDesk(desk); return; }
                            if (st.status !== 'available') return;
                            // A seat chosen from the dropdown gets its card beside the
                            // dropdown; one tapped on the plan gets it on the seat. Either
                            // way the card appears where you were just looking.
                            setListAnchor(from?.fromList ? from : null);
                            setPickedDesk(prev => prev?.id === desk.id ? null : desk);
                          }}
                          zones={parseZones(currentLocation)}
                          heightRatio={currentLocation.name === 'PP' ? 0.64 : 0.55}
                        />
                        {pickedDesk && (listAnchor || deskAnchor?.visible) && (
                          <div className="absolute z-30"
                            style={listAnchor
                              ? { left: listAnchor.leftPx, top: listAnchor.topPx, transform: 'translate(0, 6px)' }
                              : { left: `${deskAnchor.left}%`, top: `${deskAnchor.top}%`, transform: 'translate(-50%, 14px)' }}>
                            <div className="rounded-xl border border-gray-200 bg-white shadow-xl px-3 py-2.5 w-[13.5rem]">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className={`${T.strong} text-gray-900`}>{currentLocation.name} : {pickedDesk.label}</div>
                                  <div className={`${T.meta} text-gray-500`}>
                                    {pickedDates.length === 1 ? fmtLong(pickedDates[0]) : `${pickedDates.length} days selected`}
                                  </div>
                                </div>
                                {!bookingFor && (
                                  <button type="button" onClick={() => toggleFavourite(pickedDesk)}
                                    title={favourites.some(f => f.desk_id === pickedDesk.id) ? 'Remove from your desks' : 'Save as one of your desks'}
                                    className="text-[14px] leading-none text-amber-500 hover:text-amber-600">
                                    {favourites.some(f => f.desk_id === pickedDesk.id) ? '★' : '☆'}
                                  </button>
                                )}
                              </div>
                              {pickedDesk.assigned_person_name && (
                                <div className={`${T.meta} text-gray-500 mt-1`}>Normally {pickedDesk.assigned_person_name}'s</div>
                              )}
                              <div className="mt-2 flex gap-1.5">
                                {SLOTS.map(sl => (
                                  <button key={sl.value} type="button" disabled={busy}
                                    onClick={() => { setSlot(sl.value); setConfirmDesk(pickedDesk); }}
                                    className={`flex-1 px-2 py-1.5 rounded-lg ${T.meta} font-bold border transition-colors disabled:opacity-50 ${
                                      sl.value === 'full'
                                        ? 'text-white border-transparent'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                    style={sl.value === 'full' ? { background: accent } : undefined}>
                                    {sl.value === 'full' ? 'Book all day' : sl.value === 'am' ? 'AM' : 'PM'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // ── THE LIST ──
                      // The plan is the better way to choose a seat and the worse
                      // way on a phone in a corridor. Same states, same colours,
                      // grouped by area, one tap to book.
                      <div className="space-y-3">
                        {deskGroups.map(g => (
                          <div key={g.label} className="rounded-lg border border-gray-200 overflow-hidden">
                            <div className="px-2.5 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                              <span className={`${T.label} text-gray-600`}>{g.label}</span>
                              <span className={`${T.meta} font-semibold ${g.free ? 'text-emerald-700' : 'text-gray-400'}`}>
                                {g.free} of {g.desks.length} free
                              </span>
                            </div>
                            <ul className="divide-y divide-gray-100">
                              {g.desks.map(d => {
                                const st = deskState(d);
                                const free = st.status === 'available';
                                const mine = st.status === 'mine';
                                return (
                                  <li key={d.id} className="flex items-center gap-2 px-2.5 py-2">
                                    <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                      free ? 'bg-emerald-500' : mine ? 'bg-teal-600'
                                      : st.status === 'taken' ? 'bg-slate-400'
                                      : st.status === 'reserved' ? 'bg-amber-500' : 'bg-gray-300'}`} />
                                    <span className={`${T.strong} text-gray-800 w-20 flex-shrink-0`}>{d.label}</span>
                                    <span className={`${T.meta} text-gray-500 flex-1 truncate`}>
                                      {free ? 'Free'
                                        : mine ? `Yours${st.slot && st.slot !== 'full' ? ` (${String(st.slot).toUpperCase()})` : ''}`
                                        : st.status === 'taken' ? `${st.person}${st.slot && st.slot !== 'full' ? ` (${String(st.slot).toUpperCase()})` : ''}`
                                        : st.status === 'reserved' ? `Reserved${st.note ? ` — ${st.note}` : ''}`
                                        : 'Not bookable'}
                                      {d.assigned_person_name && free ? ` · normally ${d.assigned_person_name}` : ''}
                                    </span>
                                    {free && (
                                      <button type="button" disabled={busy} onClick={() => setConfirmDesk(d)}
                                        className={`${T.body} font-semibold px-3 py-1.5 rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-50 flex-shrink-0`}>
                                        Book
                                      </button>
                                    )}
                                    {mine && (
                                      <button type="button" disabled={busy} onClick={() => releaseDesk(d)}
                                        className={`${T.body} font-semibold px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0`}>
                                        Release
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                        {!deskGroups.length && (
                          <div className={`${T.body} text-gray-500 text-center py-8`}>
                            Nothing matches those filters.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Who is in, in full. A desk tile can only fit "O. Davie",
                        and with several people sharing a first name the full name
                        has to be readable somewhere without hovering. */}
                    {seatedHere.length > 0 && (
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-2.5 py-1.5 bg-gray-50 border-b border-gray-200">
                          <span className={`${T.label} text-gray-500`}>
                            In at {currentLocation.name} on {fmtShort(pickedDates[0])} · {seatedHere.length}
                          </span>
                        </div>
                        <ul className="divide-y divide-gray-100">
                          {seatedHere.map(b => (
                            <li key={b.id} className="flex items-center gap-2 px-2.5 py-1.5">
                              <Avatar as="span" personId={b.guest_name ? null : b.person_id}
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 ring-1 ring-black/5"
                                style={{ background: b.avatar_color || '#64748b' }}>
                                {(b.guest_name || b.person_name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                              </Avatar>
                              <span className={`${T.body} font-semibold text-gray-800`}>
                                {b.guest_name || b.person_name}
                                {b.guest_name && <span className="font-normal text-gray-500"> (guest of {b.person_name})</span>}
                              </span>
                              <span className={`${T.meta} text-gray-500 ml-auto`}>
                                {b.resource_name} · {b.slot === 'full' ? 'All day' : b.slot === 'am' ? 'Morning' : 'Afternoon'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <details data-tour-container="extras">
                      <summary className={`${T.body} text-gray-600 cursor-pointer font-medium`}>
                        {bookingFor
                          ? 'Repeat weekly, or star a desk'
                          : 'Repeat weekly, book for a guest, or star a desk'}
                      </summary>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div data-tour="repeat-guest">
                          <label className={`${T.body} flex items-center gap-2 cursor-pointer`}>
                            <input type="checkbox" className="w-4 h-4" checked={repeatOn} onChange={e => setRepeatOn(e.target.checked)} />
                            Repeat weekly
                          </label>
                          {repeatOn && (
                            <div className="mt-2 space-y-2">
                              <div className="flex flex-wrap gap-1">
                                {WEEKDAYS.slice(0, includeWeekend ? 7 : 5).map(d => (
                                  <button key={d.iso} type="button"
                                    onClick={() => setRepeatDays(prev => prev.includes(d.iso) ? prev.filter(x => x !== d.iso) : [...prev, d.iso])}
                                    className={`px-2.5 py-1.5 rounded border ${T.meta} font-semibold ${
                                      repeatDays.includes(d.iso) ? 'bg-navy text-white border-navy' : 'bg-white border-gray-300 hover:bg-gray-50'
                                    }`}>{d.short}</button>
                                ))}
                              </div>
                              <label className={`${T.meta} text-gray-600 flex items-center gap-2`}>
                                Until
                                <input type="date" value={repeatUntil} max={maxBookDate} min={todayIso()}
                                  onChange={e => setRepeatUntil(clampToWindow(e.target.value, maxBookDate))}
                                  className={`${T.body} border border-gray-300 rounded-md px-2 py-1.5`} />
                              </label>
                              <p className={`${T.meta} text-gray-500`}>
                                Cannot run past {fmtShort(maxBookDate)}, the end of the booking window.
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          {/* A visitor and a colleague are different things. The
                              server takes a guest name OR a person, and given
                              both would hold the seat under the visitor's name
                              and silently drop the colleague, so the field is
                              not offered while the mode is on. */}
                          {!bookingFor && (
                            <input value={guestName} onChange={e => setGuestName(e.target.value)}
                              placeholder="Guest name, to seat a visitor"
                              className={`${T.body} w-full border border-gray-300 rounded-md px-2.5 py-2`} />
                          )}
                          <div className="flex flex-wrap gap-1" data-tour="favourite-desks">
                            {!bookingFor && desks.filter(d => d.status !== 'blocked').map(d => {
                              const fav = favourites.some(f => f.desk_id === d.id);
                              return (
                                <button key={d.id} type="button" onClick={() => toggleFavourite(d)}
                                  className={`px-2 py-1 rounded border ${T.meta} font-medium ${
                                    fav ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                  }`}>{fav ? '★' : '☆'} {d.label}</button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </details>
                  </>
                )}
              </div>
            </div>

            {/* Desks only when acting for somebody else: their van booking is
                not this mode's business, and listing it here with a Cancel
                beside it would make it so. */}
            <MyUpcoming
              bookings={bookingFor ? myBookings.filter(b => b.resource_type === 'desk') : myBookings}
              onCancel={cancelBooking} busy={busy}
              title={bookingFor ? `${bookingFor.first_name}'s upcoming desks` : 'My upcoming bookings'} />
          </div>

          {/* Right rail */}
          <div className="space-y-3">
            <div data-tour="mini-calendar">
            <MiniCalendar
              selected={pickedDates[0]}
              maxDate={maxBookDate}
              minDate={todayIso()}
              marked={markedDays}
              onSelect={(d) => { setWeekStart(startOfWeek(d)); pickDate(d); }}
            />
            </div>
            <Panel title="Key">
              <div className="space-y-1.5">
                {statuses.map(st => (
                  <div key={st.code} className={`flex items-center gap-2 ${T.meta} text-gray-600`}>
                    <span className={`inline-block w-3.5 h-3.5 rounded ${tone(st.tone).dot}`} />
                    {st.icon} {st.label}
                  </div>
                ))}
                <div className={`flex items-center gap-2 ${T.meta} text-gray-600`}>
                  <span className={`inline-block w-3.5 h-3.5 rounded ${tone('off').dot}`} />
                  🌴 On leave
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── VANS ── */}
      {tab === 'vans' && (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          <div className="space-y-3">
            <Panel title="Dates">
              <div className="flex flex-wrap gap-1.5">
                {visibleDates.map(d => (
                  <button key={d} type="button" disabled={beyondWindow(d)} onClick={() => pickDate(d)}
                    title={beyondWindow(d) ? 'Beyond the booking window' : undefined}
                    className={`px-2.5 py-2 rounded-md border ${T.body} font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      pickedDates.includes(d) ? 'bg-navy text-white border-navy' : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}>
                    <div>{fmtDay(d)}</div>
                    <div className={`${T.meta} opacity-80`}>{fmtShort(d)}</div>
                  </button>
                ))}
              </div>
            </Panel>
            <Panel title="Slot">
              <div className="flex gap-1.5">
                {SLOTS.map(s => <Btn key={s.value} active={slot === s.value} onClick={() => setSlot(s.value)} className="flex-1">{s.label}</Btn>)}
              </div>
            </Panel>
            <Panel title="Where are you going?">
              <textarea value={vanNotes} onChange={e => setVanNotes(e.target.value)} rows={3}
                placeholder="Destination or purpose"
                className={`${T.body} w-full border border-gray-300 rounded-md p-2.5 resize-none`} />
            </Panel>
            <Btn disabled={busy || !selectedVan} onClick={bookVan} active className="w-full justify-center">
              {selectedVan ? `Book ${vans.find(v => v.id === selectedVan)?.name || 'van'}` : 'Select a van'}
            </Btn>
          </div>

          <div className="space-y-2">
            {vans.length === 0 ? (
              <Panel>
                {loadFailed.vans ? (
                  <div className={`${T.body} text-red-700 text-center py-10 space-y-2`}>
                    <div>Couldn't load the vans.</div>
                    <Btn onClick={retryLoad} disabled={retrying}>{retrying ? 'Trying again…' : 'Try again'}</Btn>
                  </div>
                ) : (
                  <div className={`${T.body} text-gray-500 text-center py-10`}>No vans available</div>
                )}
              </Panel>
            ) : vans.map(van => {
              // Only admin and HR are sent disabled vans (to manage them). Mark
              // them, or a van nobody else can see looks bookable here.
              const off = !van.is_active;
              const onDays = pickedDates.map(d => ({
                date: d,
                who: bookings
                  .filter(b => b.resource_id === van.id && b.booking_date === d && slotsOverlap(b.slot, slot))
                  .map(b => `${b.person_name}${b.notes ? ` (${b.notes})` : ''}`),
              })).filter(x => x.who.length);
              return (
                <button
                  key={van.id}
                  type="button"
                  disabled={off}
                  onClick={() => setSelectedVan(v => v === van.id ? '' : van.id)}
                  className={`w-full text-left bg-white rounded-lg border p-3 transition-colors ${
                    off ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : selectedVan === van.id ? 'border-navy ring-1 ring-navy' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`${T.strong} text-gray-800`}>{van.name}</div>
                      {van.description && <div className={`${T.meta} text-gray-500`}>{van.description}</div>}
                      {off && (
                        <div className={`${T.meta} text-gray-500`}>
                          Not bookable, and hidden from everyone but admin and HR. Turn it back on in Desk &amp; Van Management, Vehicles.
                        </div>
                      )}
                    </div>
                    <span className={`${T.meta} font-semibold shrink-0 ${
                      off ? 'text-gray-500' : onDays.length ? 'text-amber-700' : loadFailed.bookings ? 'text-gray-500' : 'text-emerald-700'
                    }`}>
                      {off ? 'Disabled'
                        : onDays.length ? `Booked on ${onDays.length} of your dates`
                        : loadFailed.bookings ? 'Availability unknown' : 'Free on your dates'}
                    </span>
                  </div>
                  {onDays.length > 0 && (
                    <ul className={`${T.meta} text-gray-600 mt-1.5 space-y-0.5`}>
                      {onDays.map(x => <li key={x.date}>{fmtShort(x.date)}: {x.who.join(', ')}</li>)}
                    </ul>
                  )}
                </button>
              );
            })}
            <MyUpcoming bookings={myBookings.filter(b => b.resource_type === 'van')} onCancel={cancelBooking} busy={busy} title="My van bookings" />
          </div>
        </div>
      )}

      {/* ── TEAM ── */}
      {tab === 'team' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`${T.meta} text-gray-600 flex items-center gap-2`}>
              Team
              <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
                className={`${T.body} border border-gray-300 rounded-md px-2.5 py-1.5`}>
                <option value="">Everyone</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <span className={`${T.meta} text-gray-500`}>{planner.people?.length || 0} people</span>
          </div>

          <WeekPlanner
            dates={planner.dates || []}
            people={planner.people || []}
            days={planner.days || {}}
            currentUserId={user?.id}
            canEditOthers={canBookForOthers}
            // Your own cell takes you to that day on the desks tab. Somebody
            // else's does the same thing on their behalf, which is what made
            // their cells clickable in the first place: the affordance was here
            // before the mode was, and clicking one did nothing at all.
            onCellClick={(personId, date) => {
              if (personId === user?.id) { setPickedDates([date]); setTab('week'); return; }
              if (!canBookForOthers) return;
              setPickedDates([date]);
              setTab('week');
              startBookingFor(personId);
            }}
            emptyMessage="No people in this team"
          />

          <Panel title="Key">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {statuses.concat([{ code: 'leave', label: 'On leave', icon: '🌴', tone: 'off' }]).map(s => (
                <span key={s.code} className={`inline-flex items-center gap-1.5 ${T.meta} text-gray-600`}>
                  <span className={`inline-block w-3.5 h-3.5 rounded ${tone(s.tone).dot}`} />
                  {s.icon} {s.label}
                </span>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── WHERE ARE YOU ON THIS DAY ──
          Opened by the icon slot on a day tile. Short on purpose: home, offsite,
          not working, and the offices for a day you are in without a seat. Booking
          a desk states the office by itself, which is most days, so this is the
          occasional answer and no longer a panel taking up the screen for it. */}
      {whereMenu && (() => {
        const d = whereMenu;
        const dates = whereDates(d);
        const w = myDay(d);
        // Close first: the answer lands on the tile the menu was opened from, and
        // leaving the dialog up over it hides the thing that just changed.
        const set = (fn) => { setWhereMenu(null); fn(); };
        return (
          <Modal
            title={dates.length === 1 ? `Set ${fmtLong(d)}` : `Set ${dates.length} selected days`}
            onClose={() => setWhereMenu(null)}
            footer={<Btn onClick={() => setWhereMenu(null)} disabled={busy}>Close</Btn>}
          >
            {/* ── ALL DAY, OR HALF OF IT ──
                The same halves desks book in, so "home in the morning, office in
                the afternoon" is two answers rather than an impossibility. */}
            <div className="flex gap-1.5 mb-3">
              {[{ value: 'full', label: 'All day' }, { value: 'am', label: 'Morning' }, { value: 'pm', label: 'Afternoon' }].map(s => (
                <Btn key={s.value} disabled={busy} active={whereSlot === s.value}
                  onClick={() => setWhereSlot(s.value)} className="flex-1">{s.label}</Btn>
              ))}
            </div>

            {/* Everything that is not an office needs no disambiguating. */}
            <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {statuses.filter(st => st.code !== 'office').map(st => (
                <Btn key={st.code} disabled={busy}
                  active={w?.source === 'explicit' && w?.code === st.code}
                  onClick={() => set(() => setWhereabouts(dates, st.code, null, null, whereSlot))}>
                  {st.icon} {st.label}
                </Btn>
              ))}
            </div>

            {/* ── IN THE OFFICE — BUT WHICH ONE ──
                One button per office, because "in the office" on its own does not answer
                the question somebody asked it for: they are deciding whether to come in,
                and that depends on where everyone else will be. Booking a desk sets this
                by itself; these are for the days you are in without holding a seat. */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className={`${T.label} text-gray-500 mb-1.5`}>In the office</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {officeStatus && desklessOffices.map(loc => {
                  const here = w?.code === 'office' && w?.detail?.includes(loc.name);
                  return (
                    <Btn key={loc.id} disabled={busy} active={!!here}
                      onClick={() => set(() => setWhereabouts(dates, officeStatus.code, null, loc.id, whereSlot))}
                      title={`At ${loc.name}, where there is no desk to book`}>
                      {officeStatus.icon} {loc.name}
                    </Btn>
                  );
                })}
                {/* Named one by one, same as the offices above, because "which office"
                    is the question being asked either way. What differs is the answer:
                    these open the desk selector on that office rather than recording
                    the day, so they carry an arrow to say they go somewhere. */}
                {deskOffices.map(loc => (
                  <Btn key={loc.id} disabled={busy}
                    onClick={() => set(() => goPickADesk(dates, loc.id))}
                    title={`Book a desk at ${loc.name}`}>
                    {officeStatus?.icon} {loc.name} →
                  </Btn>
                ))}
                {w && w.source !== 'leave' && w.source !== 'usual' && w.source !== 'plow' && (
                  <Btn disabled={busy} onClick={() => set(() => clearDay(dates))}>
                    {w.source === 'desk' || w.source === 'van' ? 'Clear, and cancel the booking' : 'Clear'}
                  </Btn>
                )}
              </div>
            </div>

            {/* ── EVERY WEEK ──
                The day somebody works from home every week, set once rather than
                answered each Friday. Shown as home on the planner whenever the
                day has no other answer, and it opens a reserved desk to others a
                week at a time. */}
            {(() => {
              const ticked = homeDays ? homeDays.split(',') : [];
              const myDesk = locations.flatMap(l => l.desks || [])
                .find(desk => desk.status === 'reserved' && desk.assigned_person_id === user?.id);
              return (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className={`${T.label} text-gray-500 mb-1.5`}>Working from home every week</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {WEEKDAYS.slice(0, 5).map(d => {
                      const key = d.short.toLowerCase();
                      return (
                        <Btn key={key} disabled={busy} active={ticked.includes(key)}
                          aria-pressed={ticked.includes(key)} onClick={() => toggleHomeDay(key)}>
                          {d.short}
                        </Btn>
                      );
                    })}
                    {homeDaysMsg && (
                      <span role="status" className={`${T.meta} font-semibold ${homeDaysMsg.kind === 'error' ? 'text-red-700' : 'text-emerald-700'}`}>
                        {homeDaysMsg.text}
                      </span>
                    )}
                  </div>
                  <p className={`${T.meta} text-gray-500 mt-1.5`}>
                    {!myDesk
                      ? 'Shown as working from home on the planner, unless you say otherwise for the day.'
                      : !(myDesk.auto_release_on_leave ?? 1)
                        ? `Shown as working from home on the planner. ${myDesk.label} is always locked, so it stays yours on these days.`
                        : `Shown as working from home on the planner, and ${myDesk.label} opens to others on these days a week at a time: each Monday opens the following week. Until then it is still yours, and booking it for the day keeps it.`}
                  </p>
                </div>
              );
            })()}

            <p className={`${T.meta} text-gray-500 mt-3`}>
              Being at an office with desks means holding one, so those take you to the desk plan and
              booking a seat sets the office for you.
              Saying you are anywhere other than an office releases any desk you were holding for
              that part of the day: a half-day answer gives up that half, and a full-day desk
              shrinks to the other half rather than being cancelled.
            </p>
          </Modal>
        );
      })()}

      {/* ── REPEAT THIS WEEK ──
          The week is spelled out before it is repeated, day by day, because the
          thing being copied is half a screen of tiles and a number of weeks on
          its own says nothing about what is actually going to be booked. */}
      {repeatWeekOpen && (
        <Modal
          title={`Repeat ${fmtRange(visibleDates[0], visibleDates[visibleDates.length - 1])}`}
          onClose={() => setRepeatWeekOpen(false)}
          footer={
            <>
              <Btn onClick={() => setRepeatWeekOpen(false)} disabled={busy}>Cancel</Btn>
              <Btn active disabled={busy} onClick={repeatThisWeek}>Repeat</Btn>
            </>
          }
        >
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
              {weekPlan.map(day => (
                <div key={day.date} className="flex items-center justify-between gap-3 px-2.5 py-1.5">
                  <span className={`${T.body} font-semibold text-gray-800`}>{fmtDay(day.date)} {fmtShort(day.date)}</span>
                  <span className={`${T.meta} text-gray-600 text-right`}>
                    {day.seats.length
                      ? day.seats.map(s => `${s.resource_name}${s.slot === 'full' ? '' : ` (${s.slot.toUpperCase()})`}`).join(', ')
                      : day.stated?.label}
                  </span>
                </div>
              ))}
            </div>
            <label className={`${T.body} flex items-center gap-2 text-gray-700`}>
              For the next
              <input type="number" min={1} max={maxRepeatWeeks} value={repeatWeeks}
                onChange={e => setRepeatWeeks(Math.min(Math.max(1, Number(e.target.value) || 1), maxRepeatWeeks))}
                className={`${T.body} w-16 border border-gray-300 rounded-md px-2 py-1.5`} />
              week{repeatWeeks === 1 ? '' : 's'}
            </label>
            <p className={`${T.meta} text-gray-500`}>
              Up to {maxRepeatWeeks} week{maxRepeatWeeks === 1 ? '' : 's'}: bookings run no further than {fmtShort(maxBookDate)}.
              A day somebody else has already taken is skipped and named afterwards.
            </p>
          </div>
        </Modal>
      )}

      {/* Confirm before taking a seat, or offer the switch when one is already
          held. Asking "Confirm booking?" for a booking that cannot be made, and
          then reporting the refusal in a banner at the top of the page where
          nobody scrolled back to look for it, told people the wrong thing twice
          over. The one thing they need to know is in the dialog they are already
          looking at, and the button does what they actually wanted. */}
      {confirmDesk && (() => {
      const held = heldAgainst(confirmDesk);
      const heldDesks = [...new Set(held.map(b => b.resource_name))].join(', ');
      return (
        <Modal
          title={held.length ? `Switch to ${confirmDesk.label}?` : `Book ${confirmDesk.label}?`}
          onClose={() => setConfirmDesk(null)}
          footer={
            <>
              <Btn onClick={() => setConfirmDesk(null)} disabled={busy}>
                {held.length ? `Keep ${heldDesks}` : 'Cancel'}
              </Btn>
              <Btn active disabled={busy}
                onClick={() => { const d = confirmDesk; setConfirmDesk(null); bookDesk(d, held); }}>
                {held.length ? 'Switch booking' : 'Confirm booking'}
              </Btn>
            </>
          }
        >
          {held.length > 0 && (
            <div className={`mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 ${T.body} text-amber-900`}>
              <div className={T.strong}>
                You already have {heldDesks} for {held.length === 1 ? slotWord(held[0].slot) : 'those days'}.
              </div>
              <div className={`${T.meta} mt-1`}>
                Switching releases {held.length === 1 ? 'it' : 'them'} and books {confirmDesk.label} instead.
                {held.length > 1 && (
                  <ul className="mt-1 space-y-0.5">
                    {held.map(b => (
                      <li key={b.id}>{fmtShort(b.booking_date)}: {b.resource_name}{b.location_name ? ` · ${b.location_name}` : ''} ({slotWord(b.slot)})</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <dl className="space-y-2">
            {[
              ['Desk', `${confirmDesk.label}${currentLocation ? ` · ${currentLocation.name}` : ''}`],
              ['When', pickedDates.length === 1
                ? fmtLong(pickedDates[0])
                : `${pickedDates.length} days — ${pickedDates.map(fmtShort).join(' · ')}`],
              ['Slot', SLOTS.find(s => s.value === slot)?.label || slot],
              ['For', guestName.trim()
                ? `${guestName.trim()} (guest of you)`
                : bookingFor ? bookingFor.name : 'You'],
              ...(repeatOn && repeatDays.length
                ? [['Repeats', `Weekly on ${WEEKDAYS.filter(w => repeatDays.includes(w.iso)).map(w => w.short).join(', ')}, until ${fmtShort(repeatUntil)}`]]
                : []),
            ].map(([label, value]) => (
              <div key={label} className="flex gap-3">
                <dt className={`${T.label} text-gray-500 w-20 shrink-0`}>{label}</dt>
                <dd className={`${T.body} text-gray-800 font-medium`}>{value}</dd>
              </div>
            ))}
          </dl>
          {confirmDesk.notes && (
            <div className={`${T.meta} text-gray-500 mt-3`}>{confirmDesk.notes}</div>
          )}
        </Modal>
      );
      })()}

      {/* The guided walkthrough. Auto-opens once per person, replayable from
          the header; finishing or skipping records it server-side so it never
          auto-opens again.

          The context is what the tour's conditional steps are judged against:
          the step about booking for a colleague is dropped for anybody without
          the control, rather than shown with nothing to point at. */}
      {tour.open && (
        <FeatureTour steps={deskBookingTourSteps} context={{ canBookForOthers }}
          onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}

// ─── My upcoming bookings ────────────────────────────────────────────────────
function MyUpcoming({ bookings, onCancel, busy, title = 'My upcoming bookings' }) {
  const upcoming = useMemo(
    () => (bookings || []).filter(b => b.booking_date >= todayIso()).sort((a, b) => a.booking_date.localeCompare(b.booking_date)),
    [bookings],
  );
  if (!upcoming.length) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-tour="my-upcoming">
      <div className="px-3 py-2 border-b border-gray-200">
        <span className={`${T.label} text-gray-500`}>{title}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Date', 'What', 'Where', 'Slot', ''].map((h, i) => (
                <th key={i} className={`${T.label} text-gray-500 px-3 py-2 text-left`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {upcoming.map(b => (
              <tr key={b.id} className="hover:bg-gray-50/60">
                <td className={`${T.body} text-gray-700 px-3 py-2 whitespace-nowrap`}>{fmtShort(b.booking_date)}</td>
                <td className={`${T.body} font-semibold text-gray-800 px-3 py-2`}>
                  {b.resource_name}
                  {b.guest_name && <span className={`${T.meta} text-gray-500 font-normal`}> for {b.guest_name}</span>}
                </td>
                <td className={`${T.body} text-gray-600 px-3 py-2`}>{b.location_name || (b.resource_type === 'van' ? 'Van' : '—')}</td>
                <td className={`${T.body} text-gray-600 px-3 py-2`}>{b.slot === 'full' ? 'All day' : b.slot.toUpperCase()}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" disabled={busy} onClick={() => onCancel(b)}
                    className={`${T.meta} font-medium text-red-600 hover:text-red-800 disabled:opacity-50`}>Cancel</button>
                  {b.series_id && (
                    <button type="button" disabled={busy} onClick={() => onCancel(b, true)}
                      className={`${T.meta} font-medium text-red-600 hover:text-red-800 disabled:opacity-50 ml-3`}>
                      Cancel series
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
